// src/oura/ui/Bento.tsx
//
// Rows of cards that share one height. One column below 1024px (below 1280px
// for `c2xl` and `c21xl`).

import type { ReactNode } from "react";
import { cn } from "../lib/cn";

const LAYOUT = {
    c2: "lg:grid-cols-2",
    c3: "lg:grid-cols-3",
    // A wide-left pair: the list gets a little more, and the side column
    // stays wide enough (about 480px) to read its sentences in full.
    c21: "lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]",
    // A pair that splits only from 1280px: a list of long rows beside a
    // stack, which at 1024px would squeeze the list to a few words a line.
    c2xl: "xl:grid-cols-2",
    // The wide-left pair, split only from 1280px: a list of sentences beside
    // a stack, each column too narrow at 1024 to read a sentence in 2 lines.
    c21xl: "xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]",
} as const;

export function Bento({ layout, children, className }: { layout: keyof typeof LAYOUT; children: ReactNode; className?: string }) {
    return (
        <div className={cn("grid grid-cols-1 items-stretch gap-5 [&>*]:min-w-0", LAYOUT[layout], className)}>
            {children}
        </div>
    );
}

/** Two or more cards stacked in one bento cell, 20px apart, each at its own height. */
export function BentoStack({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn("flex min-w-0 flex-col gap-5", className)}>{children}</div>;
}

/**
 * Spans two bento columns from 1024px. A grid of its own, so the card inside
 * stretches to the row like any bento cell, and a folded one still does not.
 */
export function Span2({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn("grid min-w-0 grid-cols-1 lg:col-span-2", className)}>{children}</div>;
}
